import { useEffect, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router'
import { Stack, Card, CircularProgress } from '@mui/material'
import { useStytchB2BClient, useStytchMemberSession } from '@stytch/react/b2b'
import { OrganizationBySlugMatch } from '@stytch/vanilla-js'
import {
  getGlobalBaseUrl,
  getOrgSlug,
  sanitizeReturnTo,
  updateUrlWithSubdomain,
} from '@/utils/auth'
import AuthenticateAccounts from '@/components/auth/AuthenticateAccounts'
import ForgotPassword from '@/components/auth/ForgotPassword'
import Login from '@/components/auth/Login'

export default function Authenticate() {
  const stytch = useStytchB2BClient()
  const { session, fromCache } = useStytchMemberSession()
  const [stytchOrg, setStytchOrg] = useState<OrganizationBySlugMatch>()

  const [searchParams] = useSearchParams()

  useEffect(() => {
    const orgSlugInParams = searchParams.get('org_slug')
    const orgSlugInUrl = getOrgSlug()
    // If org_slug in params differs from that in URL, update URL to match params
    if (
      orgSlugInParams &&
      (!orgSlugInUrl || orgSlugInParams !== orgSlugInUrl)
    ) {
      window.location.replace(updateUrlWithSubdomain(orgSlugInParams))
    }
  }, [searchParams])

  useEffect(() => {
    if (!stytchOrg) {
      const orgSlug = getOrgSlug()
      if (orgSlug) {
        stytch.organization
          .getBySlug({ organization_slug: orgSlug })
          .then(({ organization }) => {
            if (organization) {
              setStytchOrg(organization)
            } else {
              window.location.replace(getGlobalBaseUrl(orgSlug))
            }
          })
      }
    }
  }, [stytchOrg, stytch.organization])

  if (session) {
    const returnTo = searchParams.get('return_to')
    return (
      // TODO: Think of Lite version, too
      <Navigate
        to={returnTo ? sanitizeReturnTo(returnTo) : '/projects'}
        replace
      />
    )
  }

  if (getOrgSlug() && !stytchOrg) {
    return (
      <Stack alignItems="center">
        <CircularProgress size={80} />
      </Stack>
    )
  }

  // If fromCache is true, but no session, the Member object is from
  // the cache and a state refresh is in progress. (from docs: https://stytch.com/docs/b2b/sdks/members/get-member)
  if (!session && fromCache) {
    return null
  }

  const redirectType = searchParams.get('stytch_redirect_type')

  return (
    <Stack alignItems="center" justifyContent="center" sx={{ height: '100vh' }}>
      {searchParams.has('token') ? (
        redirectType === 'reset_password' ? (
          <Card elevation={2} sx={{ p: 6, width: 450 }}>
            <ForgotPassword />
          </Card>
        ) : (
          <AuthenticateAccounts stytchOrg={stytchOrg} />
        )
      ) : (
        <Card elevation={2} sx={{ p: 6, width: 450 }}>
          <Login stytchOrg={stytchOrg} />
        </Card>
      )}
    </Stack>
  )
}
